import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import type { ComponentProps, ReactNode } from 'react'
import _PriceRowPrice from '../_PriceRowPrice'
import clsx from 'clsx'
import { defineMessage, useIntl } from 'react-intl'

type InheritedBasePriceRowPriceProps = Pick<ComponentProps<typeof _PriceRowPrice>, 'className' | 'discountPercentage' | 'discountType' | 'saleUnit' | 'signDisplay' | 'size' | 'priceFormat' | 'caption'>

interface Props extends InheritedBasePriceRowPriceProps, Utils.PropsWithDataAttributes {
  /**
   * Main price
   */
  memberPrice: number
  /**
   * Optional regular (non-member) price. When provided and higher than the
   * member price, it is rendered as a leading strike-through before the member
   * price (e.g. `A$7,600` A$7,500 with LuxPlus+).
   */
  nonMemberPrice?: number
  /** Defaults to the existing leading strike-through. */
  nonMemberPricePlacement?: 'leading' | 'trailing'
  /** Render the treatment logo without "with" or the legacy leading-logo spacer. */
  hideLogoPrefix?: boolean
  /**
   * Prepend the price with the LuxPlus logo
   */
  logoPlacement?: 'leading' | 'trailing'
}

const trailingLuxPlusMessage = defineMessage({
  id: 'qg18mjM_yahXRtEZVoVD',
  defaultMessage: 'with <luxPlusLogo></luxPlusLogo>',
})

const renderLuxPlusLogo = (_chunks: ReactNode) => <LuxPlusLogoWrapper height={8} colour="inherit" />
const trailingLuxPlusMessageValues = { luxPlusLogo: renderLuxPlusLogo }

function PriceRowLuxPlusPrice(props: Props) {
  const {
    memberPrice,
    nonMemberPrice,
    nonMemberPricePlacement = 'leading',
    hideLogoPrefix = false,
    logoPlacement,
    saleUnit,
    size,
    className,
    ...rest
  } = props
  const intl = useIntl()

  const showStrikeThrough = nonMemberPrice != null && nonMemberPrice > memberPrice
  const strikeThroughPrice = showStrikeThrough && <BodyText as="span" variant="medium" colour="light-foreground-alt" strikeThrough>
    <FormatCurrency value={nonMemberPrice} format="roundedDollar" signDisplay={nonMemberPricePlacement === 'trailing' ? rest.signDisplay : undefined} />
  </BodyText>
  const showLeadingStrikeThrough = showStrikeThrough && nonMemberPricePlacement === 'leading'
  const priceLeading = (logoPlacement === 'leading' || showLeadingStrikeThrough) && <>
    {logoPlacement === 'leading' && <><LuxPlusLogoWrapper height={8} />{!hideLogoPrefix && '\u00a0'}</>}
    {showLeadingStrikeThrough && strikeThroughPrice}
  </>

  return <_PriceRowPrice
    {...rest}
    className={clsx(className, PriceRowLuxPlusPrice.name)}
    size={size}
    price={memberPrice}
    priceColour="lux-plus"
    saleUnitColour="lux-plus"
    priceLeading={priceLeading}
    saleUnit={saleUnit}
    priceTrailing={<>
      {nonMemberPricePlacement === 'trailing' && strikeThroughPrice}
      {logoPlacement === 'trailing' && <Caption
        as="span"
        variant="medium"
        colour="lux-plus"
        weight="normal"
        format="lowercase"
        wrap="no-wrap"
      >
        {hideLogoPrefix ? <LuxPlusLogoWrapper height={8} colour="inherit" /> : intl.formatMessage(trailingLuxPlusMessage, trailingLuxPlusMessageValues)}
      </Caption>}
    </>}
    wrap={logoPlacement === 'trailing' || nonMemberPricePlacement === 'trailing' ? 'word-wrap' : undefined}
  />
}

export default PriceRowLuxPlusPrice
